import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import type { AppliedCoupon } from '@/features/campaigns/types/campaign';

/* ── Types ── */

export type CampaignMediaItem = {
  mediaId?: string;
  mediaUrl?: string;
  mediaType?: string;
  orientation?: 'PORTRAIT' | 'LANDSCAPE' | string;
};

export type CampaignBillboard = {
  billboardId: string;
  screenId?: string;
  screenName?: string;
  screenType?: string;
  screenOrientation?: string;
  totalPrice?: number;
  totalDates?: number;
  totalSlots?: number;
  assignedRepetitions?: number;
  address?: {
    city?: string;
    state?: string;
    pincode?: string;
    fullAddress?: string;
  };
};

export type CampaignPricing = {
  baseAmount?: number;
  totalDiscountAmount?: number;
  totalAmount?: number;
  currency?: string;
  taxBreakdown?: Record<string, number>;
  taxDistribution?: Array<{
    role?: string;
    percentage?: number;
    totalAmount?: number;
  }>;
};

export type AgencyClientInfo = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  mobileNumber?: string;
  role?: string;
  approvedAt?: string;
  [key: string]: unknown;
};

export type CampaignOverview = {
  campaignSubmittedBy?: { firstName?: string; lastName?: string; username?: string };
  campaignName?: string;
  orderId?: string;
  startDate?: string;
  endDate?: string;
  customDuration?: number;
  totalBookedSlots?: number;
  totalRepetition?: number;
  status?: string;
  mediaIds?: CampaignMediaItem[];
  billboards?: CampaignBillboard[];
  pricing?: CampaignPricing;
  taxDistribution?: Array<{
    role?: string;
    percentage?: number;
    amount?: number;
    totalAmount?: number;
  }>;
  coupon?: AppliedCoupon;
  isQrIncluded?: boolean;
  createdAt?: string;
  qrInfo?: {
    title?: string;
    whatsAppNumber?: string;
    couponCode?: string;
    coupenCode?: string;
    productLink?: string;
    predefinedMessage?: string;
  };
  reject_reason?: string | string[];
  admin_escalation_reason?: string | string[];
  ccv_forward_reason?: string | string[];
  lcm_forward_reason?: string | string[];
  agencyClient?: AgencyClientInfo;
  agencyClientId?: AgencyClientInfo | string;
  clientName?: string;
  [key: string]: unknown;
};

/**
 * Matches the actual API response shape for:
 *   GET v1/common/campaign/progress
 *
 * The API always returns uniqueDates / sortedSlots / availableScreens regardless
 * of which filters are applied. KPI counts are server-computed — never derive them
 * client-side from playTimings.
 */
export type CampaignProgressData = {
  campaignId?: string;
  orderId?: string;

  // Filter metadata — always present regardless of active filters / page number
  uniqueDates?: string[];
  sortedSlots?: Array<{ hour?: number; formattedSlot?: string }>;
  availableScreens?: Array<{
    _id?: string;
    screenId?: string;
    screenName?: string;
    screenType?: string;
    address?: Record<string, string>;
  }>;

  // Active-filter echo from the backend (null when not filtered)
  date?: string | null;
  slot?: string | null;

  // ── Server-computed KPI totals ──────────────────────────────────────────────
  // Use these directly.  Do NOT recalculate from playTimings on the client.
  totalRepetitions?: number;
  successRepetitions?: number;
  pendingRepetitions?: number;
  failedRepetitions?: number;
  successRate?: number;    // 0–100 (percentage, not decimal)

  // Alias used by some API versions
  totalPlayTimings?: number;

  slotDetails?: {
    hour?: number | null;
    date?: string | null;
    repetitions?: number;
  };

  // Pagination object — use this to drive infinite-scroll page detection
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;       // items on this page
    totalPages?: number;  // total pages for the current filter set
  };

  playTimings?: Array<{
    time?: any;
    date?: any;
    formattedTime?: string;
    status?: 'SUCCESS' | 'PENDING' | 'FAILED' | 'EXPIRED' | 'APPROVED' | 'PLAYED' | string;
  }>;

};

export type CampaignAnalyticsData = {
  footfallCount: string | number;
  maleCount?: number;
  femaleCount?: number;
  peopleCount?: number;
  audienceCount?: number;
  dwellTime?: number;
  engagementScore?: number;
  vehicleDistribution?: Record<string, number>;
  vehicles?: Record<string, number>;
};

export type AgeDistributionItem = {
  range?: string;
  count?: number;
};

/* ── Query hooks ── */

export function useCampaignOverviewQuery(campaignId?: string) {
  return useQuery({
    queryKey: [...queryKeys.campaigns.root, 'overview', campaignId ?? ''],
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.campaigns.overview, {
        params: { campaignId },
      });
      return data as { success?: boolean; data?: CampaignOverview };
    },
    enabled: Boolean(campaignId),
    refetchOnWindowFocus: false,
  });
}

export function useCampaignScreensQuery(campaignId?: string) {
  return useQuery({
    queryKey: [...queryKeys.campaigns.root, 'screens', campaignId ?? ''],
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.campaigns.screens, {
        params: { campaignId },
      });
      return data as {
        success?: boolean;
        data?: {
          billboards?: CampaignBillboard[];
          pricing?: { currency?: string };
        };
      };
    },
    enabled: Boolean(campaignId),
    refetchOnWindowFocus: false,
  });
}

export function useCampaignProgressQuery(
  campaignId?: string,
  filters?: { date?: string; slot?: string; screenId?: string },
) {
  const filterKey = [filters?.date ?? '', filters?.slot ?? '', filters?.screenId ?? ''];
  return useQuery({
    queryKey: [...queryKeys.campaigns.root, 'progress', campaignId ?? '', ...filterKey],
    queryFn: async () => {
      const params: Record<string, unknown> = { campaignId };
      if (filters?.date) params.date = filters.date;
      if (filters?.slot) params.slot = filters.slot;
      if (filters?.screenId) params.screenId = filters.screenId;
      const { data } = await httpClient.get(endpoints.campaigns.progress, { params });
      return data as { success?: boolean; data?: CampaignProgressData };
    },
    enabled: Boolean(campaignId),
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetches filter metadata (availableScreens, uniqueDates, sortedSlots) once
 * without any active filters. This keeps the dropdown options stable while the
 * user is selecting different screen / date / slot combinations.
 *
 * limit:1 is intentional — the API always returns all filter metadata regardless
 * of page size, so we only need 1 timing row to get everything we need.
 */
export function useProgressMetaQuery(campaignId?: string) {
  return useQuery({
    queryKey: [...queryKeys.campaigns.root, 'progress-meta', campaignId ?? ''],
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.campaigns.progress, {
        params: { campaignId, page: 1, limit: 1 },
      });
      return data as { success?: boolean; data?: CampaignProgressData };
    },
    enabled: Boolean(campaignId),
    staleTime: 5 * 60_000,   // 5 min — options don't change while viewing the tab
    refetchOnWindowFocus: false,
  });
}

export function useInfiniteCampaignProgressQuery(
  campaignId?: string,
  filters?: { date?: string; slot?: string; screenId?: string },
  limit = 48,
) {
  // Flatten filter values into the key so React Query correctly detects changes
  // when the user picks a different screen / date / slot.  Using an object
  // literal would use reference equality and would NEVER trigger a refetch.
  const filterKey = [
    filters?.date ?? '',
    filters?.slot ?? '',
    filters?.screenId ?? '',
  ];

  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.campaigns.root,
      'progress-infinite',
      campaignId ?? '',
      ...filterKey,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, unknown> = {
        campaignId,
        page: pageParam,
        limit,
      };
      // Only send non-empty filter params.  Sending an empty string can
      // cause the backend to apply an unintended "no date" / "no slot" filter.
      if (filters?.date) params.date = filters.date;
      if (filters?.slot) params.slot = filters.slot;
      if (filters?.screenId) params.screenId = filters.screenId;

      const { data } = await httpClient.get(endpoints.campaigns.progress, { params });
      return data as { success?: boolean; data?: CampaignProgressData };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // Primary: use the server-provided pagination object.
      const pg = lastPage?.data?.pagination;
      if (pg?.page != null && pg?.totalPages != null) {
        return pg.page < pg.totalPages ? pg.page + 1 : undefined;
      }

      // Fallback: compare loaded count vs server total
      // (used when the pagination object is absent)
      return undefined;
    },
    enabled: Boolean(campaignId),
    refetchOnWindowFocus: false,
  });
}

export function useCampaignAnalyticsQuery(campaignId?: string) {
  return useQuery({
    queryKey: [...queryKeys.campaigns.root, 'analytics', campaignId ?? ''],
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.campaigns.analytics, {
        params: { campaignId },
      });
      return data as { success?: boolean; data?: CampaignAnalyticsData };
    },
    enabled: Boolean(campaignId),
    refetchOnWindowFocus: false,
  });
}

export function useAgeDistributionQuery(campaignId?: string) {
  return useQuery({
    queryKey: [...queryKeys.campaigns.root, 'age-distribution', campaignId ?? ''],
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.campaigns.ageDistribution, {
        params: campaignId ? { campaignId } : {},
      });
      return data as {
        success?: boolean;
        data?: AgeDistributionItem[];
      };
    },
    enabled: Boolean(campaignId),
    refetchOnWindowFocus: false,
  });
}
